import React from 'react';
import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { LocaleProvider } from '../../i18n/LocaleContext';
import { useSeo, SEO } from '../../seo/useSeo';
import { breadcrumbJsonLd } from '../../seo/breadcrumbs';
import { APP_STORE_URL, googlePlayUrl, isEventId } from '../../constants/storeLinks';
import { isIosDevice, writeClipboardToken } from '../../utils/installIntent';
import '../../styles/landing.css';

export const EventPage = (): React.JSX.Element => {
    const [event, setEvent] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const { id } = useParams();
    const [searchParams] = useSearchParams();

    const apiUrl = searchParams.get("env") === "dev"
        ? "https://api.gather-community.com/dev/api"
        : "https://api.gather-community.com/api";

    useEffect(() => {
        fetch(`${apiUrl}/events/${id}`)
            .then((res) => res.json())
            .then(setEvent)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id, apiUrl]);

    const eventTitle = event?.name as string | undefined;
    const eventDescription = (event?.description as string | undefined)?.slice(0, 200);
    const eventImage = event?.imageUrl as string | undefined;

    useSeo({
        title: eventTitle ?? 'Event',
        description: eventDescription ?? 'Join this event on Gather — discover hyperlocal events, clubs, and meetups near you.',
        canonical: `/event/${id}`,
        ogType: 'event',
        ogImage: eventImage,
        noindex: !event,
        jsonLd: event
            ? [
                  breadcrumbJsonLd([
                      { name: 'Home', path: '/' },
                      { name: 'Events', path: '/' },
                      { name: eventTitle ?? 'Event', path: `/event/${id}` },
                  ]),
                  {
                      '@context': 'https://schema.org',
                      '@type': 'Event',
                      name: eventTitle,
                      description: eventDescription,
                      image: eventImage,
                      startDate: event.startDate,
                      url: `${SEO.SITE_URL}/event/${id}`,
                      eventStatus: 'https://schema.org/EventScheduled',
                      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
                      location: event.location
                          ? {
                                '@type': 'Place',
                                name: event.location.address,
                                address: {
                                    '@type': 'PostalAddress',
                                    streetAddress: event.location.address,
                                    addressLocality: event.location.city,
                                },
                            }
                          : undefined,
                      organizer: {
                          '@type': 'Organization',
                          name: 'Gather',
                          url: SEO.SITE_URL,
                      },
                  },
              ]
            : undefined,
    });

    if (loading) return (
        <LocaleProvider>
            <div className="landing" id="top">
                <Header />
                <main>
                    <div style={styles.container}>Loading...</div>
                </main>
                <Footer />
            </div>
        </LocaleProvider>
    );

    if (!event) return (
        <LocaleProvider>
            <div className="landing" id="top">
                <Header />
                <main>
                    <div style={styles.container}>Event not found</div>
                </main>
                <Footer />
            </div>
        </LocaleProvider>
    );

    const date = new Date(event.startDate).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    return (
        <LocaleProvider>
            <div className="landing" id="top">
                <Header />
                <main>
                    <div style={styles.container}>
                        {event.imageUrl && (
                            <img src={event.imageUrl} alt={event.name} style={styles.image} />
                        )}
                        <h1>{event.name}</h1>
                        <p>{date} at {event.startTime}</p>
                        <p>{event.location?.address}, {event.location?.city}</p>
                        <p>{event.description}</p>

                        <button
                            onClick={() => {
                                // Clipboard fallback for iOS deferred deep linking: the app checks
                                // the pasteboard for this token on first launch. Written here because
                                // the button tap is the user gesture browsers require for a write.
                                // iOS only — Android uses the install referrer and desktop has no
                                // app, so writing there would only clobber the user's clipboard.
                                if (isIosDevice() && isEventId(id)) writeClipboardToken(id);

                                const deepLink = `gather://event/${id}`;
                                const isAndroid = /Android/i.test(navigator.userAgent || '');
                                // Android carries the event id to Play as an install referrer, so
                                // the app opens this event on its first launch after the install.
                                const storeLink = isAndroid ? googlePlayUrl(id) : APP_STORE_URL;

                                // Try to open the app
                                window.location.href = deepLink;

                                // If the app is not installed, redirect to the right store after a short delay.
                                // When the app *did* open, the browser tab is backgrounded — bail out then,
                                // otherwise the user comes back to a Play Store page they never asked for.
                                setTimeout(() => {
                                    if (document.hidden) return;
                                    window.location.href = storeLink;
                                }, 500);
                            }}
                            style={styles.button}
                        >
                            Open in App
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        </LocaleProvider>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {
        maxWidth: 600,
        margin: "0 auto",
        padding: 24,
        fontFamily: "system-ui, sans-serif",
    },
    image: {
        width: "100%",
        borderRadius: 12,
        marginBottom: 16,
    },
    button: {
        display: "inline-block",
        marginTop: 24,
        padding: "14px 28px",
        backgroundColor: "#000",
        color: "#fff",
        borderRadius: 12,
        textDecoration: "none",
        fontWeight: 600,
        border: "none",
        cursor: "pointer",
    },
};
